import { useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { parseTasksToOverview, taskOverviewConfig, TaskOverviewTab } from 'entities';

import { Tabs, Skeleton, Column, Row } from 'components/Blocks';
import { ListManipulation } from 'components/ListManipulation';
import { SortFilterButtons } from 'components/Buttons';

import TaskUsers from './Users';
import TaskHeader from './Header';
import TaskDate from './Date';
import { ProjectLink } from './ProjectLink';

import { useProject } from 'providers/Project';
import { useUrlStateV2, useUser } from 'hooks';
import { useTeam } from 'providers/Team';

import { getTaskFilters } from './filters';

export default function TasksOverview() {
  const { t } = useLang();

  const [{ tab }, setQuery] = useUrlStateV2({
    tab: {
      defaultValue: 'today' as TaskOverviewTab,
      validate: (v) => ['overdue', 'today', 'week', 'month'].includes(v),
    },
  });

  const { teamUsers, loadingUsers } = useTeam();
  const { projects, loadingProjects } = useProject();
  const loggedUser = useUser();

  const tasks = useMemo(
    () =>
      parseTasksToOverview({
        loggedUser,
        projects: Object.values(projects),
        teamUsers,
      })[tab],
    [projects, teamUsers, loggedUser, tab],
  );

  const filterParams = useMemo(
    () => ({
      filters: getTaskFilters(projects),
    }),
    [projects],
  );

  return (
    <ListManipulation itens={tasks} storageKey="task-ov-@@" {...filterParams}>
      {({ parsedItens, hasFilter, openFilters }) => (
        <Column>
          <Row justifyContent="space-between" className="p-1 phone:flex-col">
            <Row gap="2">
              <h5 className="text-primary">{t('tasks-overview')}</h5>

              <SortFilterButtons hasFilter={hasFilter} onFilterClick={openFilters} />
            </Row>

            <Tabs
              type="highlighted"
              tabListClassName="small_tablet:flex-wrap h-auto"
              onTabChange={(tab) => setQuery({ tab: tab as TaskOverviewTab })}
              value={tab}
              tabs={Object.values(taskOverviewConfig).map((config) => ({
                ...config,
                value: config.id,
              }))}
            />
          </Row>

          <Column>
            {loadingProjects || loadingUsers ? (
              <Skeleton height={105} quantity={4} />
            ) : (
              parsedItens.map((task) => (
                <div
                  key={`${tab}-${task.id}-overview-page`}
                  className={[
                    'mid_tablet:grid-cols1 grid grid-cols-2 gap-4 rounded-md bg-card p-5 text-card-foreground shadow-blue',
                    '[&>*:nth-child(even)]:justify-self-end mid_tablet:[&>*:nth-child(even)]:justify-self-start [&>*]:w-fit',
                  ].join(' ')}
                >
                  <TaskHeader task={task} />

                  <TaskDate task={task} />

                  <TaskUsers task={task} />

                  <ProjectLink task={task} />
                </div>
              ))
            )}
          </Column>

          {!tasks.length && !loadingProjects && !loadingUsers && (
            <h5 className="w-full text-center">{t('no-tasks-met-your-search-criterea')}</h5>
          )}
        </Column>
      )}
    </ListManipulation>
  );
}
